import { useEffect } from 'react';
import { getLocation } from './helper';
import { fetchTodayMissed } from '../../services/apis/dispense/todayMissed';

import * as routeNames from '../../routes/routeName'

import { getAblyRealtime } from '../../networking/ablyClient';
import { subscribeToDeliveredDispenseSocketUpdates } from '../../services/realtime/realtimeSocket';

// The backend publishes delivered-dispense over BOTH Ably and the Socket.IO server
// (Ably is unreliable in the Android WebView, the socket is not), so each subscription
// listens on both and de-dupes: whichever transport arrives first wins. The payload has
// no unique id, so we key on its identifying fields within a short window. The deduper
// is created per subscription, so separate hooks still each get the event.
const makeDeliveryDeduper = (windowMs = 15000) => {
    const seen = new Map();
    return (data) => {
        const key = [
            data?.patient_id,
            data?.auth_driver_id,
            data?.delivery_source,
            data?.target_driver_id,
        ].join('|');
        const now = Date.now();
        seen.forEach((at, k) => {
            if (now - at > windowMs) seen.delete(k);
        });
        if (seen.has(key)) return true;
        seen.set(key, now);
        return false;
    };
};

const getDeliveredByOtherMessage = (data = {}) => {
    const patientName = data?.patient_name ? `${data.patient_name} ` : 'This patient ';
    if (data?.delivery_source === 'instore_app') {
        return `${patientName}medication was delivered by the In-Store.`;
    }

    return `${patientName}was delivered by another delivery user.`;
};

const shouldNotifyAssignedDriverForInstoreDelivery = (data = {}, driverID) => {
    if (data?.delivery_source !== 'instore_app') {
        return false;
    }

    return Number(data?.target_driver_id) === Number(driverID);
};

export const TodayDispenseDeliveredByAnotherDriverAblyHelper = ({
    apiKey,
    deliveryDriverDeliveredDispense,
    pharmacyId,
    driverID,
    pId,
    routeName,
    location,
    setPatientID,
    setDeliveryDoneByAnotherDriver,
    dispatch,
    removeTodayDispenePatientById,
    setOpenIndex,
    setPId,
    setCordinate
}) => {


    useEffect(() => {
        getLocation(setCordinate)
    }, [])

    useEffect(() => {
        if (!apiKey) return undefined;
        const ably = getAblyRealtime();
        if (!ably) return undefined;
        const channel = ably.channels.get(deliveryDriverDeliveredDispense);
        const eventName = `delivered-dispense-${pharmacyId}`;
        const isDuplicate = makeDeliveryDeduper();

        const listener = (message) => {
            if (message?.name === eventName && location.pathname.startsWith(routeName.DRIVER_ROUTE_TODAY)) {
                if (!shouldNotifyAssignedDriverForInstoreDelivery(message?.data, driverID)) {
                    return;
                }
                if (isDuplicate(message?.data)) return;

                const toastMessage = getDeliveredByOtherMessage(message?.data);
                setDeliveryDoneByAnotherDriver(toastMessage)
                setTimeout(() => {
                    setDeliveryDoneByAnotherDriver(false)
                }, 5000);

                setPatientID(message?.data?.patient_id)

                if (pId === message?.data?.patient_id) {
                    dispatch(removeTodayDispenePatientById(message?.data?.patient_id));
                    setPId(null)
                    setPatientID(null)
                    setOpenIndex(null)

                } else {
                    dispatch(removeTodayDispenePatientById(message?.data?.patient_id));
                    setOpenIndex(null)
                    setPId(null)
                    setPatientID(null)
                }
            }
        };
        channel.subscribe(eventName, listener);
        // Parallel Socket.IO path (works on Android where Ably does not). The socket
        // delivers the raw payload, so shape it like an Ably message to reuse `listener`.
        const socketSub = subscribeToDeliveredDispenseSocketUpdates({
            pharmacyId,
            onEvent: (payload) => listener({ name: eventName, data: payload }),
        });
        return () => {
            // Shared app-wide client: unsubscribe only, never close it (closing would
            // kill the connection handover/video calls also rely on).
            try { channel.unsubscribe(eventName, listener); } catch (_) {}
            socketSub?.close();
        }
    }, [pId, pharmacyId]);

    return null;
};

export const PendingDispenseDeliveredByAnotherDriverAblyHelper = ({
    apiKey,
    deliveryDriverDeliveredDispense,
    pharmacyId,
    driverID,
    pId,
    setPatientID,
    setDeliveryDoneByAnotherDriver,
    dispatch,
    removePatientById,
    setOpenIndex,
    setPId,
    setCordinate
}) => {


    useEffect(() => {
        getLocation(setCordinate)
    }, [])


    useEffect(() => {
        if (!apiKey) return undefined;
        const ably = getAblyRealtime();
        if (!ably) return undefined;
        const channel = ably.channels.get(deliveryDriverDeliveredDispense);
        const eventName = `delivered-dispense-${pharmacyId}`;
        const isDuplicate = makeDeliveryDeduper();

        const listener = (message) => {
            if (message?.name === eventName) {
                if (!shouldNotifyAssignedDriverForInstoreDelivery(message?.data, driverID)) {
                    return;
                }
                if (isDuplicate(message?.data)) return;

                const toastMessage = getDeliveredByOtherMessage(message?.data);
                setDeliveryDoneByAnotherDriver(toastMessage)
                setTimeout(() => {
                    setDeliveryDoneByAnotherDriver(false)
                }, 5000);
                setPatientID(message?.data?.patient_id)

                if (pId === message?.data?.patient_id) {
                    dispatch(removePatientById(message?.data?.patient_id));
                    setPId(null)
                    setPatientID(null)
                    setOpenIndex(null)

                } else {
                    dispatch(removePatientById(message?.data?.patient_id));
                    setOpenIndex(null)
                    setPId(null)
                    setPatientID(null)
                }
            }
        };
        channel.subscribe(eventName, listener);
        // Parallel Socket.IO path (works on Android where Ably does not).
        const socketSub = subscribeToDeliveredDispenseSocketUpdates({
            pharmacyId,
            onEvent: (payload) => listener({ name: eventName, data: payload }),
        });
        return () => {
            // Shared app-wide client: unsubscribe only, never close it.
            try { channel.unsubscribe(eventName, listener); } catch (_) {}
            socketSub?.close();
        }
    }, [pId, pharmacyId]);

    return null;
};

export const TodayMissedDeliveredByAnotherDriverAblyHelper = ({
    apiKey,
    deliveryDriverDeliveredDispense,
    pharmacyId,
    dispatch,
    setCordinate,
    location,
    setLoading,
    driverID,
    tab = 'your',
}) => {

    useEffect(() => {
        getLocation(setCordinate)
    }, [])

    useEffect(() => {
		if (!apiKey) return undefined;
		const ably = getAblyRealtime();
		if (!ably) return undefined;
		const channel = ably.channels.get(deliveryDriverDeliveredDispense);
		const eventName = `delivered-dispense-${pharmacyId}`;
		const isDuplicate = makeDeliveryDeduper();

		const listener = (message) => {
			if (message?.name === eventName && location.pathname.startsWith(routeNames.Missed_Route)) {

				// Skip refetch when current driver confirmed delivery (already refetched in confirmDelivery)
				if (message?.data?.auth_driver_id === driverID) return;
				if (isDuplicate(message?.data)) return;
				if (pharmacyId) {
					const params = {
						pharmacy_id: pharmacyId,
						status: 'today_missed',
						tab,
					};
					setLoading(false)
					dispatch(fetchTodayMissed(params))
				}
			}
		};
		channel.subscribe(eventName, listener);
		// Parallel Socket.IO path (works on Android where Ably does not).
		const socketSub = subscribeToDeliveredDispenseSocketUpdates({
			pharmacyId,
			onEvent: (payload) => listener({ name: eventName, data: payload }),
		});
		return () => {
			// Shared app-wide client: unsubscribe only, never close it.
			try { channel.unsubscribe(eventName, listener); } catch (_) {}
			socketSub?.close();
		}
	}, [apiKey, deliveryDriverDeliveredDispense, dispatch, driverID, location.pathname, pharmacyId, setLoading, tab]);

    return null;
};

export const FinalDeliveredByAnotherDriverAblyHelper = ({
    apiKey,
    deliveryDriverDeliveredDispense,
    pharmacyId,
    dispatch,
    setCordinate,
    location,
    setLoading,
    driverID,
    tab = 'your',
}) => {

    useEffect(() => {
        getLocation(setCordinate)
    }, [])

    useEffect(() => {
		if (!apiKey) return undefined;
		const ably = getAblyRealtime();
		if (!ably) return undefined;
		const channel = ably.channels.get(deliveryDriverDeliveredDispense);
		const eventName = `delivered-dispense-${pharmacyId}`;
		const isDuplicate = makeDeliveryDeduper();

		const listener = (message) => {
			if (message?.name === eventName && location.pathname.startsWith(routeNames.driver_Missed_Route)) {
				// Skip refetch when current driver confirmed delivery (already refetched in confirmDelivery)
				if (message?.data?.auth_driver_id === driverID) return;
				if (isDuplicate(message?.data)) return;
				if (pharmacyId) {
					const params = {
						pharmacy_id: pharmacyId,
						status: 'final_missed',
						tab,
					};
					setLoading(false)
					dispatch(fetchTodayMissed(params))
				}
			}
		};
		channel.subscribe(eventName, listener);
		// Parallel Socket.IO path (works on Android where Ably does not).
		const socketSub = subscribeToDeliveredDispenseSocketUpdates({
			pharmacyId,
			onEvent: (payload) => listener({ name: eventName, data: payload }),
		});
		return () => {
			// Shared app-wide client: unsubscribe only, never close it.
			try { channel.unsubscribe(eventName, listener); } catch (_) {}
			socketSub?.close();
		}
	}, [apiKey, deliveryDriverDeliveredDispense, dispatch, driverID, location.pathname, pharmacyId, setLoading, tab]);

    return null;
};


export const SearchPatientListDeliveredByAnotherDriverAblyHelper = ({
    apiKey,
    deliveryDriverDeliveredDispense,
    pharmacyId,
    driverID,
    dispatch,
    setCordinate,
    setLoading,
    patientId,
    removePatientById,
    setDeliveryDoneByAnotherDriver,
    onDeliveredElsewhere,
}) => {

    useEffect(() => {
        getLocation(setCordinate)
    }, [])

    useEffect(() => {

        if (!apiKey) return undefined;
        const ably = getAblyRealtime();
        if (!ably) return undefined;
        const channel = ably.channels.get(deliveryDriverDeliveredDispense);
        const eventName = `delivered-dispense-${pharmacyId}`;
        const isDuplicate = makeDeliveryDeduper();

        const listener = (message) => {
            if (message?.name === eventName) {
                if (message?.data?.auth_driver_id === driverID) {
                    return;
                }
                if (isDuplicate(message?.data)) return;
                if (String(message?.data?.patient_id) === String(patientId)) {
                    setDeliveryDoneByAnotherDriver(getDeliveredByOtherMessage(message?.data))
                    setTimeout(() => {
                        setDeliveryDoneByAnotherDriver(false)
                    }, 5000);
                    if (typeof setLoading === 'function') {
                        setLoading(true)
                    }
                    if (typeof onDeliveredElsewhere === 'function') {
                        onDeliveredElsewhere(message)
                    } else {
                        dispatch(removePatientById(message?.data?.patient_id))
                    }
                }
            }
        };
        channel.subscribe(eventName, listener);
        // Parallel Socket.IO path (works on Android where Ably does not).
        const socketSub = subscribeToDeliveredDispenseSocketUpdates({
            pharmacyId,
            onEvent: (payload) => listener({ name: eventName, data: payload }),
        });

        return () => {
            // Shared app-wide client: unsubscribe only, never close it.
            try { channel.unsubscribe(eventName, listener); } catch (_) {}
            socketSub?.close();
        }
    }, [driverID, patientId, pharmacyId])

    return null;
};

export const getStatusSpan = (status, bgColor, textColor) => (
    <span className={`px-2 py-1.5 text-sm  rounded-md bg-${bgColor}-600 bg-opacity-10 text-${textColor}-900 w-auto flex justify-center items-center`}>
        {status}
    </span>
);
